import {createSelector} from 'reselect'

import {SETTINGS_PAGE} from '../../common/constants/Pages.js'
import {
  ACCOUNTING,
  API_KEYS,
  BARCODE_SCANNERS,
  BILLING,
  GENERAL,
  PACKING_LISTS,
  PAYMENTS,
  PRESETS,
  PRINTING,
  PURCHASE_ORDERS,
  RETURN_ORDER_CODES,
  RULES,
  SALES_CHANNELS,
  SCALES,
  SETTINGS,
  SHIPPERS,
  SHIPPING_INSURANCE,
  SUPPLIERS,
  THREE_P_L,
  USERS,
  WAREHOUSES,
  CONTACTS,
  SETTINGS_PANEL_OPTIONS,
} from '../../common/constants/SettingsPanels.js'
import {currentPageSelector} from '../../redux/selectors/ui/index.js'
import {
  canManageUserPermissionSelector,
  hasSettingsPermissionSelector,
  pinnedSettingsPagesSelector,
} from '../../data/me.js'
import {locationSelector} from '../../redux/selectors/ui/location.js'
import {buildPath} from '../../common/querystring.js'
import {getNounsFromValue} from '../../common/tokenizeOptions.js'

const settingsPanelOptions = SETTINGS_PANEL_OPTIONS.map((option) => {
  const path = option.path || [SETTINGS, option.value]

  return {
    ...option,
    dropdownDisplay: option.dropdownDisplay || option.display,
    path,
    url: '#' + buildPath(path),
    nouns: getNounsFromValue(
      `${option.value} ${option.display} ${option.section} ${option.additionalNouns || ''}`,
    ),
    canPin: option.canPin !== false,
    activeSelector:
      option.activeSelector ||
      ((state) => {
        const {pathComponents} = locationSelector(state)

        if (
          option.value === GENERAL &&
          pathComponents.length === 1 &&
          pathComponents[0] === path[0]
        ) {
          return true
        }

        if (
          option.topLevel &&
          pathComponents.length === 2 &&
          pathComponents[0] === path[0] &&
          pathComponents[1] === path[1]
        ) {
          return true
        }

        return path.reduce((prev, part, index) => {
          if (prev && pathComponents[index] !== part) {
            return false
          }

          return prev
        }, true)
      }),
    includeSelector: option.includeSelector || (() => true),
    disabledSelector:
      option.disabledSelector ||
      ((state) => !settingsPermissionsSelector(state)[option.value]),
  }
})

export const TOP_LEVEL_SETTINGS_PANELS = [
  ...new Set(settingsPanelOptions.map((option) => option.path[1])),
]

export const SETTINGS_SECTION_OPTIONS = [
  ...new Set(settingsPanelOptions.map((option) => option.section)),
].map((section) => ({value: section, display: section}))

export const settingsSelector = createSelector(
  (state) => state.ui,
  ({settings}) => settings,
)

export const currentSettingsPanelSelector = createSelector(
  locationSelector,
  ({pathComponents: [resource, panel]}) =>
    resource === SETTINGS
      ? TOP_LEVEL_SETTINGS_PANELS.includes(panel)
        ? panel
        : GENERAL
      : null,
)

export function getSettingsPermissions(hasSettingsPermission, canManageUser) {
  return {
    [ACCOUNTING]: hasSettingsPermission,
    [API_KEYS]: canManageUser,
    [BARCODE_SCANNERS]: hasSettingsPermission,
    [BILLING]: hasSettingsPermission,
    [GENERAL]: hasSettingsPermission,
    [CONTACTS]: hasSettingsPermission,
    [PACKING_LISTS]: hasSettingsPermission,
    [PAYMENTS]: hasSettingsPermission,
    [PRESETS]: hasSettingsPermission,
    [PRINTING]: hasSettingsPermission,
    [PURCHASE_ORDERS]: hasSettingsPermission,
    [RETURN_ORDER_CODES]: hasSettingsPermission,
    [RULES]: hasSettingsPermission,
    [SALES_CHANNELS]: hasSettingsPermission,
    [SCALES]: hasSettingsPermission,
    [SHIPPERS]: hasSettingsPermission,
    [SHIPPING_INSURANCE]: true,
    [SUPPLIERS]: hasSettingsPermission,
    [THREE_P_L]: true,
    [USERS]: true,
    [WAREHOUSES]: hasSettingsPermission,
  }
}

export const settingsPermissionsSelector = createSelector(
  hasSettingsPermissionSelector,
  canManageUserPermissionSelector,
  getSettingsPermissions,
)

export function inSettingsSelector(state) {
  return currentPageSelector(state) === SETTINGS_PAGE
}

export function settingsPanelOptionsSelector(state) {
  const pinned = pinnedSettingsPagesSelector(state)

  return settingsPanelOptions
    .filter(({includeSelector}) => includeSelector(state))
    .map((option) => ({
      ...option,
      disabled: option.disabledSelector(state),
      active: option.activeSelector(state),
      pinned: pinned.includes(option.value),
    }))
}

export function settingsDropdownOptionsSelector(state) {
  const pinned = pinnedSettingsPagesSelector(state)
  const allOptions = settingsPanelOptionsSelector(state)
  const options = allOptions.filter((option) => pinned.includes(option.value))

  // general settings is number one
  options.unshift(allOptions[0])

  return options
}
